import { useRef, useState } from 'react';
import { QuestionMarkCircleIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { createChat, streamMessage, type Source } from '../api/unimedica';
import MarkdownContent from './MarkdownContent';

/** Fragen zur offenen Akte — direkt neben der Akte, nicht auf einer anderen
 *  Seite. Fuer lange Gespraeche bleibt der Chat; hier geht es um die kurze
 *  Rueckfrage beim Blaettern. (Port aus AkteFragen.svelte) */
type Eintrag = { frage: string; antwort: string; quellen: Source[]; laeuft: boolean };
const VORSCHLAEGE = ['Welche aktuellen Diagnosen?', 'Letzte Laborwerte?', 'Welche Medikamente?'];

export default function AkteFragen({ pid, patientName = '' }: { pid: string; patientName?: string }) {
  const [verlauf, setVerlauf] = useState<Eintrag[]>([]);
  const [frage, setFrage] = useState('');
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const chatIdRef = useRef<string | null>(null);

  const patch = (i: number, fn: (e: Eintrag) => Eintrag) =>
    setVerlauf((v) => v.map((e, j) => (j === i ? fn(e) : e)));

  const fragen = async (text: string) => {
    const t = text.trim();
    if (!t || beschaeftigt) return;
    setFrage(''); setFehler(null); setBeschaeftigt(true);
    let i = 0;
    setVerlauf((v) => { i = v.length; return [...v, { frage: t, antwort: '', quellen: [], laeuft: true }]; });
    try {
      if (!chatIdRef.current) {
        const c = await createChat({ mode: 'patient', patient_id: pid, title: `Akte ${patientName || pid}` });
        chatIdRef.current = c.id;
      }
      await streamMessage(
        chatIdRef.current, t,
        (stueck) => patch(i, (e) => ({ ...e, antwort: e.antwort + stueck })),
        (meta) => { patch(i, (e) => ({ ...e, laeuft: false, quellen: (meta?.sources ?? []) as Source[] })); setBeschaeftigt(false); },
        (err) => { patch(i, (e) => ({ ...e, laeuft: false })); setFehler('Die Antwort kam nicht an. Bitte erneut versuchen.'); setBeschaeftigt(false); console.warn('Akten-Frage:', err); },
      );
    } catch (e) {
      patch(i, (x) => ({ ...x, laeuft: false }));
      setFehler('Die Frage konnte nicht gestellt werden.'); setBeschaeftigt(false);
    }
  };

  return (
    <div className="card overflow-hidden p-0">
      <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3">
        <QuestionMarkCircleIcon className="h-4 w-4 shrink-0 text-primary-500" />
        <span className="text-[15px] font-medium text-ps-dark-900">Fragen zu dieser Akte</span>
      </div>
      <div className="max-h-[58vh] space-y-3.5 overflow-y-auto px-4 py-3.5">
        {verlauf.length === 0 && (<>
          <p className="text-sm text-ps-dark-500">Kurze Rückfrage, ohne die Akte zu verlassen. Jede Antwort nennt das Dokument, aus dem sie stammt.</p>
          <div className="flex flex-wrap gap-1.5">
            {VORSCHLAEGE.map((v) => <button key={v} onClick={() => void fragen(v)} className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[13px] text-ps-dark-700 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700">{v}</button>)}
          </div>
        </>)}
        {verlauf.map((e, i) => (
          <div key={i}>
            <div className="text-sm font-bold text-ps-dark-900">{e.frage}</div>
            {e.antwort ? (
              <div className="mt-1 overflow-x-auto text-sm text-ps-dark-700 [&_table]:min-w-full [&_table]:text-[13px]"><MarkdownContent text={e.antwort} compact /></div>
            ) : e.laeuft && (
              <div className="mt-1.5 flex items-center gap-2">
                <span className="flex gap-1">{[0, 0.2, 0.4].map((d) => <span key={d} className="pulse-dot h-1.5 w-1.5 rounded-full bg-primary-500" style={{ animationDelay: `${d}s` }} />)}</span>
                <span className="text-sm text-ps-dark-500">Einen Moment bitte …</span>
              </div>
            )}
            {e.quellen.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {e.quellen.slice(0, 4).map((q, j) => (
                  <span key={j} className="inline-flex items-center gap-1.5 rounded border border-primary-300 bg-primary-50 px-2 py-0.5 text-[11.5px] font-semibold text-primary-700">
                    <DocumentIcon className="h-3 w-3" /> {(q as any).title ?? (q as any).filename ?? 'Dokument'}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
        {fehler && <p className="text-sm text-rose-700">{fehler}</p>}
      </div>
      <div className="flex gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3">
        <input value={frage} onChange={(e) => setFrage(e.target.value)} disabled={beschaeftigt}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void fragen(frage); } }}
          className="input-field min-w-0 flex-1 !text-sm disabled:opacity-60" placeholder="Frage zu dieser Akte …" aria-label="Frage zu dieser Akte" />
        <button onClick={() => void fragen(frage)} disabled={beschaeftigt || !frage.trim()} className="btn-primary min-h-11 shrink-0 px-4 text-sm disabled:cursor-not-allowed disabled:opacity-40">Fragen</button>
      </div>
    </div>
  );
}
